import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import SEOHead from "../components/SEOHead";
import PageHeader from "../components/PageHeader";
import KPI from "../components/KPI";
import { Button } from "../components/ui/button";
import { auth, dashApi } from "../lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const AdminDashboard = () => {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [summary, setSummary] = useState(null);
  const [leads, setLeads] = useState([]);
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    if (!auth.token()) { nav("/admin/login"); return; }
    auth.me().then(setMe).catch(() => nav("/admin/login"));
    dashApi.summary().then(setSummary);
    dashApi.leads().then(r => setLeads(r.leads || []));
    dashApi.audit().then(r => setLogs(r.logs || []));
  }, [nav]);

  const signOut = () => { auth.clear(); nav("/admin/login"); };

  return (
    <Layout>
      <SEOHead title="Admin Dashboard | AuditIQ" path="/admin" />
      <PageHeader eyebrow={`Signed in as ${me?.email || "…"}`} title="Admin Dashboard"
        subtitle="Leads, audit logs, and platform KPIs — all served over JWT-authenticated endpoints."
        actions={<Button className="btn-ghost-gold h-10 px-5" onClick={signOut} data-testid="admin-signout">Sign out</Button>}
      />
      <section className="max-w-[1400px] mx-auto px-6 py-14 grid md:grid-cols-4 gap-5">
        <KPI label="Entities" value={summary?.entities_tracked ?? "—"} testid="admin-kpi-entities" />
        <KPI label="Documents" value={summary?.budget_documents ?? "—"} testid="admin-kpi-docs" />
        <KPI label="Risk Flags" value={summary?.risk_flags_total ?? "—"} accent="warning" testid="admin-kpi-flags" />
        <KPI label="Leads" value={leads.length} accent="success" testid="admin-kpi-leads" />
      </section>
      <section className="max-w-[1400px] mx-auto px-6 pb-20">
        <Tabs defaultValue="leads">
          <TabsList className="bg-navy-deep border border-gold-dim/30">
            <TabsTrigger value="leads" data-testid="admin-tab-leads">Leads</TabsTrigger>
            <TabsTrigger value="audit" data-testid="admin-tab-audit">Audit Logs</TabsTrigger>
          </TabsList>
          <TabsContent value="leads">
            <div className="panel-elev mt-4 overflow-x-auto">
              <table className="w-full text-sm" data-testid="leads-table">
                <thead className="text-[11px] uppercase tracking-[0.2em] text-steel">
                  <tr className="border-b border-gold-dim/20">
                    <th className="text-left p-4">Created</th>
                    <th className="text-left p-4">Name</th>
                    <th className="text-left p-4">Email</th>
                    <th className="text-left p-4">Organization</th>
                    <th className="text-left p-4">Form</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map(l => (
                    <tr key={l.id} className="border-b border-gold-dim/10">
                      <td className="p-4 text-cream-dim">{new Date(l.created_at).toLocaleString()}</td>
                      <td className="p-4 text-cream">{l.name}</td>
                      <td className="p-4 text-gold font-mono text-xs">{l.email}</td>
                      <td className="p-4 text-cream-dim">{l.organization || "—"}</td>
                      <td className="p-4 text-[11px] uppercase tracking-wider text-gold">{l.form_type}</td>
                    </tr>
                  ))}
                  {!leads.length && (
                    <tr><td colSpan={5} className="p-8 text-center text-steel">No leads yet — submit the contact form to test.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>
          <TabsContent value="audit">
            <div className="panel-elev mt-4 overflow-x-auto">
              <table className="w-full text-sm" data-testid="audit-table">
                <thead className="text-[11px] uppercase tracking-[0.2em] text-steel">
                  <tr className="border-b border-gold-dim/20">
                    <th className="text-left p-4">Time</th>
                    <th className="text-left p-4">Action</th>
                    <th className="text-left p-4">Actor</th>
                    <th className="text-left p-4">Meta</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map(l => (
                    <tr key={l.id} className="border-b border-gold-dim/10">
                      <td className="p-4 text-cream-dim">{new Date(l.ts).toLocaleString()}</td>
                      <td className="p-4 text-gold font-mono text-xs">{l.action}</td>
                      <td className="p-4 text-cream">{l.actor}</td>
                      <td className="p-4 text-cream-dim text-xs">{JSON.stringify(l.meta)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </section>
    </Layout>
  );
};

export default AdminDashboard;
